import { useState, useEffect } from "react";
import { UpdateAddonSession } from "../../Api";

export default function UpdateModal({ session, onClose, onSuccess }) {
  const [sessionID, setSessionID] = useState(session.id || "");
  const [sessionName, setSessionName] = useState(session.sessionName || "");
  const [durationMinutes, setDurationMinutes] = useState(session.durationMinutes || "");
  const [price, setPrice] = useState(session.price || "");
  const [currency, setCurrency] = useState(session.currency || "");
  const [description, setDescription] = useState(session.description || "");
  const [sessionCount, setSessionCount] = useState(session.sessionCount || "1");
  const [isActive, setIsActive] = useState(session.isActive !== null ? session.isActive : true);
  const [appStoreProductId, setAppStoreProductId] = useState(session.appStoreProductId || "");

  // Smart Auto-sync: Check if existing ID matches the auto-generated pattern
  const checkInitialManual = () => {
    const expectedId = `gym_${session.sessionCount || "1"}_sessions`;
    return session.appStoreProductId && session.appStoreProductId !== expectedId;
  };
  const [isManualId, setIsManualId] = useState(checkInitialManual());
  const [errors, setErrors] = useState({});

  // Auto-generate App Store Product ID
  useEffect(() => {
    if (!isManualId || !appStoreProductId) {
      if (sessionCount) {
        setAppStoreProductId(`gym_${sessionCount}_sessions`);
        setIsManualId(false); // Reset manual flag if we are auto-generating because it was empty
      }
    }
  }, [sessionCount, isManualId, appStoreProductId]);

  const validateForm = () => {
    const newErrors = {};
    if (!sessionName.trim()) newErrors.sessionName = 'Session Name is required';

    if (!durationMinutes) newErrors.durationMinutes = 'Duration is required';
    else if (isNaN(durationMinutes) || Number(durationMinutes) <= 0) newErrors.durationMinutes = 'Duration must be valid';

    if (!price && price !== 0) newErrors.price = 'Price is required';
    else if (isNaN(price) || Number(price) < 0) newErrors.price = 'Price must be valid';

    if (!sessionCount) newErrors.sessionCount = 'Session Count is required';
    else if (isNaN(sessionCount) || Number(sessionCount) <= 0) newErrors.sessionCount = 'Count must be valid';

    if (!appStoreProductId.trim()) newErrors.appStoreProductId = 'App Store Product ID is required';
    else if (!appStoreProductId.startsWith('gym_')) newErrors.appStoreProductId = 'Must start with gym_';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;


    const result = await UpdateAddonSession(
      sessionID,
      sessionName,
      durationMinutes,
      price,
      currency,
      description,
      sessionCount,
      isActive,
      appStoreProductId
    );
    if (result.status === 200) {
      if (onSuccess) onSuccess();
      onClose();
      console.log("this is updated data ", result.data);
    }
    console.log(result);
  };

  const currencyOptions = ["INR"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 overflow-hidden rounded-lg bg-white shadow-xl border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">
              Update Addon Session
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                Save
              </button>
              <button
                onClick={onClose}
                className="rounded-full p-1 hover:bg-gray-100"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Name *
              </label>
              <input
                type="text"
                value={sessionName}
                placeholder="Enter Session Name"
                onChange={(e) => {
                  setSessionName(e.target.value);
                  if (errors.sessionName) setErrors({ ...errors, sessionName: null });
                }}
                className={`block w-full px-2 py-1.5 border rounded text-sm ${errors.sessionName ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.sessionName && <p className="text-red-500 text-xs mt-1">{errors.sessionName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                App Store Product ID * (Convention: gym_[count]_sessions)
              </label>
              <input
                type="text"
                value={appStoreProductId}
                placeholder="e.g., gym_5_sessions"
                onChange={(e) => {
                  setAppStoreProductId(e.target.value);
                  setIsManualId(true);
                  if (errors.appStoreProductId) setErrors({ ...errors, appStoreProductId: null });
                }}
                className={`block w-full px-2 py-1.5 border rounded text-sm ${errors.appStoreProductId ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.appStoreProductId && <p className="text-red-500 text-xs mt-1">{errors.appStoreProductId}</p>}
            </div>

            {/* Compact row for 3 fields */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Duration (Mins) *
                </label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => {
                    setDurationMinutes(e.target.value);
                    if (errors.durationMinutes) setErrors({ ...errors, durationMinutes: null });
                  }}
                  placeholder="Mins"
                  className={`block w-full px-2 py-1.5 border rounded text-sm ${errors.durationMinutes ? 'border-red-500' : 'border-gray-300'}`}
                  min="1"
                />
                {errors.durationMinutes && <p className="text-red-500 text-xs mt-1">{errors.durationMinutes}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Price (INR)*
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    if (errors.price) setErrors({ ...errors, price: null });
                  }}
                  placeholder="Price"
                  className={`block w-full px-2 py-1.5 border rounded text-sm ${errors.price ? 'border-red-500' : 'border-gray-300'}`}
                  min="0"
                  step="0.01"
                />
                {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              </div>

            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Count *
                </label>
                <input
                  type="number"
                  value={sessionCount}
                  onChange={(e) => {
                    setSessionCount(e.target.value);
                    if (errors.sessionCount) setErrors({ ...errors, sessionCount: null });
                  }}
                  placeholder="Count"
                  className={`block w-full px-2 py-1.5 border rounded text-sm ${errors.sessionCount ? 'border-red-500' : 'border-gray-300'}`}
                  min="1"
                />
                {errors.sessionCount && <p className="text-red-500 text-xs mt-1">{errors.sessionCount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  className="block w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                className="block w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}